// src/stock/StockCritique.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Table, Row, Col, Form, Button,
    Tooltip, OverlayTrigger
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function StockCritique() {
    const navigate = useNavigate();
    const [produits, setProduits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState('tous');
    const [sortConfig, setSortConfig] = useState({ key: 'urgence', direction: 'desc' });
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchStockCritique();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchStockCritique = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stock_etat.php?action=critique`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getNiveauAlerte = (niveau) => {
        if (niveau === 'rupture') {
            return (
                <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                    <i className="ri-close-circle-line"></i>Rupture
                </span>
            );
        }
        return (
            <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                <i className="ri-alert-line"></i>Critique
            </span>
        );
    };

    const getUrgenceLevel = (produit) => {
        if (produit.niveau_alerte === 'rupture') return 3;
        if (produit.quantite_reelle <= produit.seuil_alerte / 2) return 2;
        return 1;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children, className }) => (
        <th className={className} onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const resetFilters = () => {
        setFilterText('');
        setFilterType('tous');
    };

    /* ============================================================
       FILTER / SORT
       ============================================================ */
    const filteredProduits = produits
        .filter(p => {
            const matchesSearch = p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
                p.designation?.toLowerCase().includes(filterText.toLowerCase());
            if (filterType === 'rupture') return p.niveau_alerte === 'rupture' && matchesSearch;
            if (filterType === 'critique') return p.niveau_alerte === 'critique' && matchesSearch;
            return matchesSearch;
        })
        .sort((a, b) => {
            if (sortConfig.key === 'urgence') {
                const niveauA = getUrgenceLevel(a);
                const niveauB = getUrgenceLevel(b);
                return sortConfig.direction === 'asc' ? niveauA - niveauB : niveauB - niveauA;
            }
            if (sortConfig.key === 'quantite_reelle') {
                return sortConfig.direction === 'asc'
                    ? (a.quantite_reelle || 0) - (b.quantite_reelle || 0)
                    : (b.quantite_reelle || 0) - (a.quantite_reelle || 0);
            }
            if (sortConfig.key === 'designation') {
                return sortConfig.direction === 'asc'
                    ? a.designation?.localeCompare(b.designation)
                    : b.designation?.localeCompare(a.designation);
            }
            return 0;
        });

    const totalRupture = produits.filter(p => p.niveau_alerte === 'rupture').length;
    const totalCritique = produits.filter(p => p.niveau_alerte === 'critique').length;
    const valeurTotaleRupture = produits
        .filter(p => p.niveau_alerte === 'rupture')
        .reduce((sum, p) => sum + (p.quantite_reelle * p.prix_achat_ht || 0), 0);

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading && !produits.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Analyse des stocks critiques...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                <i className="ri-alert-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Stock critique</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> {produits.length} produit(s)
                                    </span>
                                    {totalRupture > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                            <i className="ri-close-circle-line"></i> {totalRupture} en rupture
                                        </span>
                                    )}
                                    {totalCritique > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                            <i className="ri-alert-line"></i> {totalCritique} critiques
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchStockCritique} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/etat/instantane')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-close-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Rupture de stock</span>
                                    <h3 className="kpi-value-charte">{totalRupture}</h3>
                                    <span className="kpi-unit-charte">produits épuisés</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-alert-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Stock critique</span>
                                    <h3 className="kpi-value-charte">{totalCritique}</h3>
                                    <span className="kpi-unit-charte">sous le seuil d'alerte</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Valeur en rupture</span>
                                    <h3 className="kpi-value-charte" style={{ fontSize: '1rem' }}>
                                        {formatCurrency(valeurTotaleRupture)}
                                    </h3>
                                    <span className="kpi-unit-charte">capital immobilisé</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par référence, produit..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="tous">Toutes les alertes</option>
                                        <option value="rupture">Rupture</option>
                                        <option value="critique">Critique</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2">
                                    <Form.Select
                                        value={sortConfig.key}
                                        onChange={(e) => setSortConfig({ key: e.target.value, direction: 'desc' })}
                                        className="filter-select-charte"
                                    >
                                        <option value="urgence">Trier par urgence</option>
                                        <option value="quantite_reelle">Trier par stock</option>
                                        <option value="designation">Trier par nom</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2" className="d-flex justify-content-end">
                                    <button
                                        className="btn-filter-reset-charte"
                                        onClick={resetFilters}
                                        title="Réinitialiser"
                                    >
                                        <i className="ri-refresh-line"></i>
                                    </button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-alert-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Produits critiques</span>
                                    <span className="badge-header-light">{filteredProduits.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <SortableHeader field="designation">Produit</SortableHeader>
                                            <th>Catégorie</th>
                                            <SortableHeader field="quantite_reelle" className="text-center">Stock actuel</SortableHeader>
                                            <th className="text-center">Seuil</th>
                                            <th className="text-center">Manquant</th>
                                            <th className="text-end">Valeur</th>
                                            <th className="text-center">Niveau</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProduits.length > 0 ? (
                                            filteredProduits.map((p) => {
                                                const ratio = (p.quantite_reelle / p.seuil_alerte) * 100;
                                                const urgenceLevel = getUrgenceLevel(p);
                                                return (
                                                    <tr key={p.id_produit} className={`row-critique-charte urgence-${urgenceLevel}`}>
                                                        <td className="ps-4">
                                                            <span className="reference-badge-charte">{p.reference}</span>
                                                        </td>
                                                        <td>
                                                            <Link to={`/stock/produits/${p.id_produit}`} className="table-link-charte">
                                                                {p.designation}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            {p.nom_categorie ? (
                                                                <span className="category-badge-charte">{p.nom_categorie}</span>
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center" style={{ minWidth: '150px' }}>
                                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip>Niveau: {Math.min(ratio, 100).toFixed(0)}% du seuil</Tooltip>}
                                                                >
                                                                    <div className="progress-bar-charte" style={{ width: '80px' }}>
                                                                        <div className="progress-fill-charte"
                                                                             style={{
                                                                                 width: `${Math.min(ratio, 100)}%`,
                                                                                 background: p.niveau_alerte === 'rupture' ? COLORS.danger : COLORS.warning
                                                                             }}>
                                                                        </div>
                                                                    </div>
                                                                </OverlayTrigger>
                                                                <span className={`fw-bold ${p.niveau_alerte === 'rupture' ? 'text-danger' : ''}`}>
                                                                    {p.quantite_reelle}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="quantity-badge-charte">{p.seuil_alerte}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            {p.quantite_a_commander > 0 ? (
                                                                <span className="fw-bold text-danger">
                                                                    <i className="ri-arrow-down-line me-1"></i>
                                                                    {p.quantite_a_commander}
                                                                </span>
                                                            ) : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(p.quantite_reelle * p.prix_achat_ht)}
                                                        </td>
                                                        <td className="text-center">{getNiveauAlerte(p.niveau_alerte)}</td>
                                                        <td className="text-center pe-4">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Réapprovisionner</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte action-btn-text-charte"
                                                                    onClick={() => navigate(`/stock/mouvements/ajouter/${p.id_produit}?type=entree`)}
                                                                    style={{ '--btn-color': COLORS.primary }}
                                                                >
                                                                    <i className="ri-add-line"></i>
                                                                    <span>Commander</span>
                                                                </button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte" style={{ background: 'rgba(16,185,129,0.1)', color: COLORS.success }}>
                                                            <i className="ri-checkbox-circle-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun produit en stock critique</h6>
                                                        <p className="text-muted small mb-0">
                                                            {filterText || filterType !== 'tous'
                                                                ? "Aucun résultat ne correspond à vos filtres"
                                                                : "Tous les stocks sont à un niveau satisfaisant"}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== ACTIONS RECOMMANDÉES ===== */}
                    {filteredProduits.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mt-4">
                            <Card.Header className="card-header-gradient" style={{ background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))' }}>
                                <h5 className="mb-0 text-white d-flex align-items-center">
                                    <i className="ri-lightbulb-line me-2"></i>
                                    Actions recommandées
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md={4}>
                                        <div className="action-reco-card-charte">
                                            <div className="action-reco-icon-charte" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                                <i className="ri-shopping-cart-line"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Commandes urgentes</small>
                                                <strong className="fs-5">{totalRupture + totalCritique}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="action-reco-card-charte">
                                            <div className="action-reco-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                                <i className="ri-truck-line"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Montant à commander</small>
                                                <strong className="fs-5">{formatCurrency(valeurTotaleRupture)}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="action-reco-card-charte">
                                            <div className="action-reco-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                <i className="ri-file-copy-line"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Commandes groupées</small>
                                                <strong className="fs-5">Créer</strong>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.85rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .btn-filter-reset-charte {
                    background: rgba(255,255,255,0.15);
                    border: 1.5px solid rgba(255,255,255,0.3);
                    color: white;
                    width: 40px; height: 38px;
                    border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .btn-filter-reset-charte:hover {
                    background: rgba(255,255,255,0.25);
                    border-color: rgba(255,255,255,0.5);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-critique-charte.urgence-3 { background: #fff5f5; }
                .table-charte tbody tr.row-critique-charte.urgence-2 { background: #fffbeb; }
                .table-charte tbody tr.row-critique-charte.urgence-3:hover { background: #ffe5e5; }
                .table-charte tbody tr.row-critique-charte.urgence-2:hover { background: #fef3c7; }

                .table-link-charte {
                    color: var(--primary);
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .table-link-charte:hover { text-decoration: underline; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .category-badge-charte {
                    display: inline-flex; align-items: center;
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                    padding: 0.25rem 0.65rem;
                    border-radius: 50px;
                    font-weight: 600; font-size: 0.7rem;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .quantity-badge-charte {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 45px;
                    padding: 0.25rem 0.6rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.8rem;
                    color: #475569;
                }

                /* ===== PROGRESS ===== */
                .progress-bar-charte {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill-charte {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }
                .action-btn-text-charte {
                    width: auto;
                    padding: 0 12px;
                    gap: 4px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    color: var(--primary);
                    border-color: ${COLORS.primary}40;
                }
                .action-btn-text-charte:hover {
                    background: var(--btn-color);
                    color: white;
                    border-color: var(--btn-color);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== ACTION RECO ===== */
                .action-reco-card-charte {
                    display: flex;
                    align-items: center;
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                    gap: 1rem;
                }
                .action-reco-card-charte:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0,0,0,0.06);
                    border-color: ${COLORS.primary}40;
                }
                .action-reco-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-outline:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}